import type { LogContext, LogLevel } from '@/lib/utils/logger';

type SentryLevel = Extract<LogLevel, 'warn' | 'error'>;

interface ErrorTrackingPayload {
  level: SentryLevel;
  message: string;
  context: LogContext;
  error?: Error;
}

interface SentryLike {
  captureException: (
    error: Error,
    context: {
      level: SentryLevel;
      tags: Record<string, unknown>;
      extra: LogContext;
    }
  ) => void;
  captureMessage: (
    message: string,
    context: {
      level: SentryLevel;
      tags: Record<string, unknown>;
      extra: LogContext;
    }
  ) => void;
}

interface FirebaseAnalyticsLike {
  logEvent: (eventName: string, params: Record<string, unknown>) => void;
}

interface FirebaseLike {
  analytics?: () => FirebaseAnalyticsLike;
}

type GtagLike = (command: 'event', eventName: string, params?: Record<string, unknown>) => void;

type ErrorTrackingWindow = Window &
  typeof globalThis & {
    Sentry?: SentryLike;
    gtag?: GtagLike;
    firebase?: FirebaseLike;
  };

export type ErrorTrackingSink = 'sentry' | 'gtag' | 'firebase' | 'custom-endpoint';
export type ErrorTrackingMode = 'auto' | 'explicit';
export interface VerificationDispatchResult {
  verificationId: string;
  sinks: ErrorTrackingSink[];
}

export interface ErrorTrackingStatus {
  mode: ErrorTrackingMode;
  configuredSinks: ErrorTrackingSink[];
  invalidConfiguredSinks: string[];
  availableSinks: ErrorTrackingSink[];
  activeSinks: ErrorTrackingSink[];
  missingConfiguredSinks: ErrorTrackingSink[];
}

const VALID_SINKS: ErrorTrackingSink[] = ['sentry', 'gtag', 'firebase', 'custom-endpoint'];

function getTrackingWindow(): ErrorTrackingWindow | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window as ErrorTrackingWindow;
}

function buildTags(context: LogContext): Record<string, unknown> {
  return {
    component: context.component,
    action: context.action,
    platform: context.platform,
  };
}

function getConfiguredEndpoint(): string | null {
  const endpoint = import.meta.env.VITE_ERROR_LOG_ENDPOINT;
  return typeof endpoint === 'string' && endpoint.trim() ? endpoint.trim() : null;
}

function normalizeConfiguredSink(value: string): ErrorTrackingSink | null {
  const normalized = value.trim().toLowerCase();

  if (!normalized) {
    return null;
  }

  if (normalized === 'endpoint') {
    return 'custom-endpoint';
  }

  return VALID_SINKS.includes(normalized as ErrorTrackingSink)
    ? (normalized as ErrorTrackingSink)
    : null;
}

function getConfiguredSinkConfig(): {
  configuredSinks: ErrorTrackingSink[];
  invalidConfiguredSinks: string[];
  hasExplicitConfig: boolean;
} {
  const rawValue = import.meta.env.VITE_ERROR_TRACKING_SINKS;
  if (typeof rawValue !== 'string') {
    return {
      configuredSinks: [],
      invalidConfiguredSinks: [],
      hasExplicitConfig: false,
    };
  }

  const trimmed = rawValue.trim();
  if (!trimmed || trimmed.toLowerCase() === 'auto') {
    return {
      configuredSinks: [],
      invalidConfiguredSinks: [],
      hasExplicitConfig: false,
    };
  }

  const uniqueSinks = new Set<ErrorTrackingSink>();
  const invalidConfiguredSinks = new Set<string>();

  trimmed.split(',').forEach((part) => {
    const sink = normalizeConfiguredSink(part);
    if (sink) {
      uniqueSinks.add(sink);
      return;
    }

    const invalidValue = part.trim();
    if (invalidValue) {
      invalidConfiguredSinks.add(invalidValue);
    }
  });

  return {
    configuredSinks: Array.from(uniqueSinks),
    invalidConfiguredSinks: Array.from(invalidConfiguredSinks),
    hasExplicitConfig: true,
  };
}

function getAvailableSinks(trackingWindow: ErrorTrackingWindow | null): ErrorTrackingSink[] {
  const sinks: ErrorTrackingSink[] = [];

  if (trackingWindow?.Sentry) {
    sinks.push('sentry');
  }

  if (trackingWindow?.gtag) {
    sinks.push('gtag');
  }

  if (trackingWindow?.firebase?.analytics?.()) {
    sinks.push('firebase');
  }

  if (getConfiguredEndpoint()) {
    sinks.push('custom-endpoint');
  }

  return sinks;
}

export function getErrorTrackingStatus(): ErrorTrackingStatus {
  const trackingWindow = getTrackingWindow();
  const { configuredSinks, invalidConfiguredSinks, hasExplicitConfig } = getConfiguredSinkConfig();
  const availableSinks = getAvailableSinks(trackingWindow);
  const mode: ErrorTrackingMode = hasExplicitConfig ? 'explicit' : 'auto';
  const activeSinks =
    mode === 'explicit'
      ? configuredSinks.filter((sink) => availableSinks.includes(sink))
      : availableSinks;
  const missingConfiguredSinks =
    mode === 'explicit'
      ? configuredSinks.filter((sink) => !availableSinks.includes(sink))
      : [];

  return {
    mode,
    configuredSinks,
    invalidConfiguredSinks,
    availableSinks,
    activeSinks,
    missingConfiguredSinks,
  };
}

export function dispatchErrorTracking(payload: ErrorTrackingPayload): ErrorTrackingSink[] {
  const trackingWindow = getTrackingWindow();
  const sinks: ErrorTrackingSink[] = [];
  const status = getErrorTrackingStatus();
  const tags = buildTags(payload.context);

  try {
    if (status.activeSinks.includes('sentry') && trackingWindow?.Sentry) {
      if (payload.error) {
        trackingWindow.Sentry.captureException(payload.error, {
          level: payload.level,
          tags,
          extra: payload.context,
        });
      } else {
        trackingWindow.Sentry.captureMessage(payload.message, {
          level: payload.level,
          tags,
          extra: payload.context,
        });
      }

      sinks.push('sentry');
    }
  } catch (error) {
    console.warn('Failed to send to Sentry:', error);
  }

  try {
    if (status.activeSinks.includes('gtag') && trackingWindow?.gtag) {
      trackingWindow.gtag('event', 'error', {
        error_message: payload.message,
        error_type: payload.error?.name || 'Unknown',
        error_stack: payload.error?.stack,
        component: payload.context.component,
        action: payload.context.action,
        platform: payload.context.platform,
        severity: payload.level,
      });

      sinks.push('gtag');
    }
  } catch (error) {
    console.warn('Failed to send to Google Analytics:', error);
  }

  try {
    const analytics =
      status.activeSinks.includes('firebase') ? trackingWindow?.firebase?.analytics?.() : null;
    if (analytics) {
      analytics.logEvent('app_error', {
        error_message: payload.message,
        error_type: payload.error?.name || 'Unknown',
        component: payload.context.component,
        action: payload.context.action,
        platform: payload.context.platform,
        severity: payload.level,
      });

      sinks.push('firebase');
    }
  } catch (error) {
    console.warn('Failed to send to Firebase Analytics:', error);
  }

  try {
    const endpoint = getConfiguredEndpoint();
    if (status.activeSinks.includes('custom-endpoint') && endpoint) {
      const body = JSON.stringify({
        level: payload.level,
        message: payload.message,
        error: payload.error
          ? {
              name: payload.error.name,
              message: payload.error.message,
              stack: payload.error.stack,
            }
          : null,
        context: payload.context,
        timestamp: new Date().toISOString(),
      });

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body,
      }).catch(() => {
        // Silently fail to avoid recursive logging loops.
      });

      sinks.push('custom-endpoint');
    }
  } catch {
    // Silently fail to avoid recursive logging loops.
  }

  return sinks;
}

/** Payload marker for deploy verification tests. Do not treat as real errors. */
export const VERIFICATION_TEST_MESSAGE =
  '[StackedStreak] Error tracking verification test — confirm this event in your sink dashboard';

/**
 * Dispatches a known test payload through the same path as real errors.
 * Use in deploy-like environments to confirm the chosen sink receives events.
 *
 * Call from console: `window.__stackedStreakErrorTrackingTest?.()`
 * Returns the sinks that received the test event.
 */
export function dispatchVerificationTest(): VerificationDispatchResult {
  const verificationId = `verify-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
  const message = `${VERIFICATION_TEST_MESSAGE} [id:${verificationId}]`;

  const sinks = dispatchErrorTracking({
    level: 'warn',
    message,
    context: {
      component: 'ErrorTrackingVerification',
      action: 'dispatchVerificationTest',
      platform: typeof navigator !== 'undefined' ? 'web' : 'unknown',
      verification: true,
      verificationId,
      timestamp: new Date().toISOString(),
    },
    error: new Error(message),
  });

  return {
    verificationId,
    sinks,
  };
}
